import {Ng1Controller, StateService, TargetState, Transition} from "@uirouter/angularjs";
import {DataHubService} from "../../modules/DataHubModule";
import {AssetDto} from "../../model/AssetDto";
import {QbCustomerShipToAddressDto} from "../../model/QbCustomerShipToAddressDto";
import {QbCustomerFullDto} from "../../model/QbCustomerFullDto";
import {DisposeBag} from "../../modules/DisposeBag";
import {DebounceFactory} from "../../modules/DebounceModule";
import {CustomerTagGroupDto} from "../../model/CustomerTagGroupDto";
import {IFilterService} from "angular";
import {QbCustomerEditDto} from "../../model/QbCustomerEditDto";
import {SpinnerService} from "../../modules/SpinnerModule";

export default class ViewController implements Ng1Controller {
    private customer: QbCustomerFullDto;
    private assets: AssetDto[] = [];
    private disposeBag = new DisposeBag();
    private tagGroups: CustomerTagGroupDto[];
    private tagView: { name: string; tags: { name: string; selected: boolean }[] }[];

    $onInit(): void {
        this.refreshDebounce();
    }

    uiCanExit(transition: Transition): boolean | TargetState | void | Promise<boolean | TargetState | void> {
        this.disposeBag.finalize();
        return undefined;
    }

    uiOnParamsChanged(newValues: any, $transition$: Transition): void {
        console.debug('uiOnParamsChanged', newValues, $transition$);
        this.$stateParams = Object.assign({}, this.$stateParams, newValues);
        if (newValues.customerGuid)
            this.refreshDebounce();
    }

    constructor(private $dataHub: DataHubService,
                private $debounce: DebounceFactory,
                private $filter: IFilterService,
                private $spinner: SpinnerService,
                private $state: StateService, private $stateParams: any) {
        $dataHub.onCustomer(c => {
            this.customer = c;
            this.populateTagView();
            this.$state.current.data.title = c.fullName;
        }, this.disposeBag);
        $dataHub.bindAllCustomerAsset(() => this.assets, this.disposeBag);
        $dataHub.onCustomerTagGroupUpdated(tag => {
            this.tagGroups.merge(tag, x => x.guid);
            this.populateTagView();
        })
        $dataHub.onConnected(this.refreshDebounce, this.disposeBag);
    }

    refreshDebounce = this.$debounce(() => this.refresh(), 500, false);

    refresh = () => {
        console.debug('refresh', this.$dataHub.isConnected)
        if (!this.$dataHub.isConnected) return;
        this.$dataHub.customerTagGroupSubscribeAndQuery()
            .then(ret => {
                this.tagGroups = ret;
                this.populateTagView();
            })
            .catch(this.$dataHub.defaultErrorHandler);
        this.customerLoad(this.$stateParams.customerGuid);
    };

    customerLoad = (guid) => {
        console.debug('customerLoad', guid);
        if (!this.$dataHub.isConnected) return;
        if (!guid) return;
        this.$spinner.show('Loading...')
        this.$dataHub.customerGet(guid).finally(() => this.$spinner.hide());
        this.assets = [];
        this.$dataHub.customerAssetSubscribe(guid);
    };

    autoDebitSave = () => {
        this.$dataHub.customerAutoDebitPaymentProfileUpdate(this.customer, this.customer.autoDebitPaymentProfile)
            .catch(this.$dataHub.defaultErrorHandler);
    }
    private _shipTo: QbCustomerShipToAddressDto;

    get shipTo(): QbCustomerShipToAddressDto {
        if (this._shipTo) return this._shipTo;
        return this.customer?.shipToAddresses?.find(s => s.defaultShipTo);
    }

    set shipTo(value: QbCustomerShipToAddressDto) {
        this._shipTo = value;
    }

    get tags(): string[] {
        let notes = this.customer?.notes;
        if (!notes) return [];
        //console.debug(notes);
        let tags = new Set<string>();
        let lastTagStart = 0;
        let x = 0;
        while (true) {
            let tagStart = notes.indexOf("[", lastTagStart);
            if (tagStart < 0) break;
            lastTagStart = tagStart + 1;
            let tagEnd = notes.indexOf("]", tagStart);
            let tagStr = notes.substring(tagStart + 1, tagEnd);
            //console.debug(tagStart, tagEnd, tagStr);
            tagStr.split(',').forEach(t => tags.add(t));
            x++;
            if (x > 10) break;
        }
        tags.delete('');
        return [...tags];
    }

    populateTagView() {
        let activeTags = this.tags;
        if (!this.tagGroups) return;
        this.tagView = this.tagGroups
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map(group => ({
                name: group.name,
                tags: group.tags.map(tag => ({
                    name: tag.name,
                    selected: activeTags.includes(tag.name)
                }))
                    .sort((a, b) => {
                        return ('' + a.name).localeCompare(b.name);
                    })

            }));
    }

    tagAdd(tag: string) {
        let tags = this.tags;
        tags.push(tag);
        this.updateTags(tags);
    }

    tagRemove(tag: string) {
        let tags = this.tags;
        tags.remove(tag, x => x);
        this.updateTags(tags);
    }

    updateTags(tags: string[]) {
        // @ts-ignore
        let tagList = this.tagView.map(i => i.tags.map(t => t.name)).flat();
        tags = tags.sort((a, b) => {
            let indexA = tagList.indexOf(a);
            let indexB = tagList.indexOf(b);
            if (indexA === -1 && indexB === -1) {
                return 0; // Treat as equal, or apply another default sort
            }
            if (indexA === -1) {
                return 1; // 'a' not in sortOrderArray, place it after 'b'
            }
            if (indexB === -1) {
                return -1; // 'b' not in sortOrderArray, place it after 'a'
            }

            return indexA - indexB;
        });

        let notes = this.customer.notes || '';
        let tagStart = notes.indexOf("[") + 1;
        if (tagStart >= 0) {
            let tagEnd = notes.indexOf("]", tagStart);
            let notes_A = notes.substring(0, tagStart - 1);
            let notes_B = notes.substring(tagEnd + 1);
            notes = notes_A + notes_B;
        }
        notes = notes + '\r\n[' + tags.join() + ']';
        notes = notes.trim();
        //console.log('new notes', notes);
        this.customer.notes = notes;
        this.saveNotes();
        this.populateTagView();
    }

    saveNotes = this.$debounce(() => {
        this.$dataHub.customerSave(<QbCustomerEditDto>this.customer);
    }, 5000, false);

    get activeTab() {
        let result = parseInt(this.$stateParams.tab);
        if(isNaN(result)) return 0;
        console.log('get activeTab', result);
        return result;
    }

    set activeTab(value) {
        console.log('set activeTab', value);
        this.$state.go('.', {tab: value}, {inherit: true});
    }
}
